// Visualises a word's relationship to the six PMS noyaux. Each noyau's size is
// derived from its percentage, which is itself derived from its integer weight
// (ponderation) relative to the total — see lib/noyaux. Read view shows only
// noyaux with >0%, sorted by weight descending (ties broken by the canonical
// display order), as uniform centred pills; the edit view (opened via the
// pencil, gated by the page edit mode) unfolds all six IN the fixed canonical
// display order, at percentage-correlated sizes with −/+ controls that
// decrement/increment the weight directly.
//
// Noyau validation (Point 8, word-detail header only — opt-in via `noyauSing`
// + `onValidate`): while `noyauValidated` is true, the pills collapse to a
// single locked `noyauSing` pill and the pencil is replaced by an un-validate
// button; while editing (not yet validated), each row's `−` is replaced by a
// `★` that opens a confirmation dialog before validating that noyau. Contexts
// that never pass `onValidate` (mosaic/sidebar compact dots, reasoning steps)
// keep the classic −/+ behaviour untouched.
import { BulkConfirmDialog } from '@/components/words/bulk-add/bulk-confirm-dialog';
import {
  decrementAt,
  incrementAt,
  noyauDisplayEntries,
  pctToFontPx,
  pctToPx,
  singleNoyauPonderation,
  sortedForDisplay,
  totalPonderation,
} from '@/lib/noyaux';
import type { NoyauCode, NoyauPonderation } from '@/schemas/word';
import { type CSSProperties, useState } from 'react';

/** Diameter (px) of the uniform pills shown in the collapsed read view. */
const PILL_PX = 40;
/** Diameter (px) of the small, non-interactive pills used in dense list rows. */
const COMPACT_PILL_PX = 10;

type ConfirmAction = { kind: 'validate'; code: NoyauCode } | { kind: 'unvalidate' };

interface RoundTagGroupProps {
  /** Per-noyau integer weights, ordered [SGP, SPG, PSG, PGS, GPS, GSP]. */
  noyauPonderation: NoyauPonderation;
  /** When true, the pencil and −/+ controls are available (gated by page edit mode). */
  editable?: boolean;
  /** Called with the next ponderation whenever the user increments/decrements a noyau. */
  onChange?: (next: NoyauPonderation) => void;
  /**
   * 'compact' renders small, non-interactive colour dots (no code/percentage
   * text, no edit affordance) for dense contexts like a search result row.
   * Defaults to 'default', the full pill treatment used on the word detail page.
   */
  size?: 'default' | 'compact';
  /** The curated dominant noyau (Tahiti `noyau_sing`) — the single pill shown once validated. */
  noyauSing?: NoyauCode | null;
  /** Whether the noyau is curated/locked (Tahiti `noyau_validated`). Ponderation editing is disabled while true. */
  noyauValidated?: boolean;
  /** Provide together with `noyauSing`/`noyauValidated` to enable the validate flow (★ replaces −). */
  onValidate?: (code: NoyauCode) => void;
  /** Called after confirming the un-validate dialog. */
  onUnvalidate?: () => void;
}

/**
 * Renders the six PMS noyau pills for a word.
 *
 * Read mode: only noyaux with a percentage above 0 are visible, as equal-sized pills.
 * Edit mode (pencil toggle): all six expand to percentage-proportional sizes with
 * −/+ controls that adjust each noyau's integer weight. `size="compact"` forces
 * read-only, text-less dots regardless of `editable`, for dense list contexts.
 */
export function RoundTagGroup({
  noyauPonderation,
  editable = false,
  onChange,
  size = 'default',
  noyauSing = null,
  noyauValidated = false,
  onValidate,
  onUnvalidate,
}: RoundTagGroupProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const compact = size === 'compact';
  const effectiveEditable = editable && !compact;
  const validating = noyauValidated && noyauSing !== null;

  // Once validated, only the single curated pill is shown — real weights hidden.
  const effectivePonderation = validating ? singleNoyauPonderation(noyauSing) : noyauPonderation;
  const total = totalPonderation(effectivePonderation);

  // Read-only and empty: nothing to show.
  if (!effectiveEditable && total === 0) return null;

  // Validation locks out expansion entirely — there is no pencil to open it (see below).
  const isExpanded = effectiveEditable && !validating && expanded;
  const pillPx = compact ? COMPACT_PILL_PX : PILL_PX;
  // Edit mode iterates the fixed canonical display order; view mode (incl.
  // compact) sorts by weight descending, ties broken by that same order.
  const entries = isExpanded
    ? noyauDisplayEntries(effectivePonderation)
    : sortedForDisplay(effectivePonderation);

  const rootClass = [
    'pms-rtg',
    compact ? 'pms-rtg--compact' : '',
    effectiveEditable ? 'pms-rtg--editable' : '',
    isExpanded ? 'pms-rtg--expanded' : '',
    total === 0 ? 'pms-rtg--empty' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClass} style={{ '--pms-rtg-pill': `${pillPx}px` } as CSSProperties}>
      {entries.map(({ code, label, index, weight, pct }) => {
        const visible = isExpanded || pct > 0;
        // Compact is always static (no expand animation) — skip zero-weight
        // noyaux entirely rather than rendering a collapsed placeholder, so
        // the flex `gap` lands evenly between only the pills actually shown.
        if (compact && !visible) return null;
        const diameter = isExpanded ? pctToPx(pct) : pillPx;
        const cellClass = `pms-rtg__cell${visible ? '' : ' pms-rtg__cell--collapsed'}`;

        return (
          <div key={code} className={cellClass}>
            <div className="pms-rtg__slot">
              <span
                className="pms-rtg__tag"
                data-noyau={code}
                aria-label={`${label} ${pct}%`}
                style={
                  {
                    width: `${diameter}px`,
                    height: `${diameter}px`,
                    fontSize: `${pctToFontPx(isExpanded ? pct : 50)}px`,
                  } as CSSProperties
                }
              >
                {!compact && (
                  <>
                    <span className="pms-rtg__code">{code}</span>
                    <span className="pms-rtg__pct">{pct}%</span>
                  </>
                )}
              </span>
            </div>

            {isExpanded && (
              <div className="pms-rtg__controls">
                {onValidate ? (
                  <button
                    type="button"
                    className="pms-rtg__step pms-rtg__step--validate"
                    aria-label={`Valider le noyau ${code}`}
                    onClick={() => setConfirmAction({ kind: 'validate', code })}
                  >
                    ★
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pms-rtg__step pms-rtg__step--minus"
                    aria-label={`Diminuer le poids de ${code}`}
                    disabled={weight === 0}
                    onClick={() => onChange?.(decrementAt(noyauPonderation, index))}
                  >
                    −
                  </button>
                )}
                <span className="pms-rtg__taglabel">{label}</span>
                <button
                  type="button"
                  className="pms-rtg__step pms-rtg__step--plus"
                  aria-label={`Augmenter le poids de ${code}`}
                  onClick={() => onChange?.(incrementAt(noyauPonderation, index))}
                >
                  +
                </button>
              </div>
            )}
          </div>
        );
      })}

      {effectiveEditable &&
        (validating ? (
          <button
            type="button"
            className="pms-rtg__edit-toggle pms-rtg__edit-toggle--unvalidate"
            aria-label="Annuler la validation du noyau"
            onClick={() => setConfirmAction({ kind: 'unvalidate' })}
          >
            <span className="material-symbols-rounded">lock_open</span>
          </button>
        ) : (
          <button
            type="button"
            className="pms-rtg__edit-toggle"
            aria-label={isExpanded ? 'Terminer la modification des noyaux' : 'Modifier les noyaux'}
            aria-pressed={isExpanded}
            onClick={() => setExpanded((e) => !e)}
          >
            <span className="material-symbols-rounded">{isExpanded ? 'check' : 'edit'}</span>
          </button>
        ))}

      {confirmAction?.kind === 'validate' && (
        <BulkConfirmDialog
          title={`Valider le noyau ${confirmAction.code} ?`}
          message="Cette action désactivera l'édition de la pondération."
          confirmLabel="Valider"
          onConfirm={() => {
            onValidate?.(confirmAction.code);
            setConfirmAction(null);
          }}
          onClose={() => setConfirmAction(null)}
        />
      )}
      {confirmAction?.kind === 'unvalidate' && (
        <BulkConfirmDialog
          title="Annuler la validation du noyau ?"
          message="L'édition de la pondération sera de nouveau disponible."
          confirmLabel="Annuler la validation"
          onConfirm={() => {
            onUnvalidate?.();
            setConfirmAction(null);
          }}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
